import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import EditJobModal from "./EditJobModal";

interface JobRow {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  posted_at: string;
  expiry_date: string;
  status: string;
  rejection_reason: string | null;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  is_expired: boolean;
}

interface DisplayJob {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  postedAt: string;
  expiryDate: string;
  status: string;
  rejectionReason: string | null;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  category: string;
  isExpired: boolean;
}

interface EditJobData {
  id: string;
  title: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  status: string;
  is_expired: boolean;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: { label: "Chờ duyệt", className: "bg-amber-100 text-amber-700" },
  approved: { label: "Đã duyệt", className: "bg-green-100 text-green-700" },
  rejected: { label: "Đã từ chối", className: "bg-red-100 text-red-600" },
};

const typeLabels: Record<string, string> = {
  "full-time": "Toàn thời gian",
  "part-time": "Bán thời gian",
  "contract": "Hợp đồng",
};

function mapJob(row: JobRow): DisplayJob {
  return {
    id: row.id,
    title: row.title,
    company: row.company,
    location: row.location,
    salary: row.salary,
    type: row.type,
    postedAt: row.posted_at,
    expiryDate: row.expiry_date,
    status: row.status || "pending",
    rejectionReason: row.rejection_reason || null,
    description: row.description,
    detail: row.detail,
    requirements: row.requirements,
    benefits: row.benefits,
    category: row.category,
    isExpired: row.is_expired || false,
  };
}

export default function JobsTable() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<DisplayJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected" | "expired">("all");
  const [sortBy, setSortBy] = useState("newest");
  const [expandedReason, setExpandedReason] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<EditJobData | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_jobs")
        .select("*")
        .eq("owner_id", user.id)
        .order("posted_at", { ascending: false });

      if (supabaseError) {
        setError("Không thể tải dữ liệu.");
        return;
      }

      if (data) {
        setJobs((data as JobRow[]).map(mapJob));
      }
    } catch {
      setError("Không thể tải dữ liệu.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const filtered = (() => {
    let result = jobs;
    if (filter === "expired") {
      result = jobs.filter((j) => j.isExpired);
    } else if (filter !== "all") {
      result = jobs.filter((j) => j.status === filter && !j.isExpired);
    }
    return result;
  })();

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "newest") return new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime();
    if (sortBy === "oldest") return new Date(a.postedAt).getTime() - new Date(b.postedAt).getTime();
    return 0;
  });

  const statusCounts = {
    pending: jobs.filter((j) => j.status === "pending" && !j.isExpired).length,
    approved: jobs.filter((j) => j.status === "approved" && !j.isExpired).length,
    rejected: jobs.filter((j) => j.status === "rejected" && !j.isExpired).length,
    expired: jobs.filter((j) => j.isExpired).length,
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const openEditModal = (job: DisplayJob) => {
    setEditingJob({
      id: job.id,
      title: job.title,
      location: job.location,
      salary: job.salary,
      type: job.type,
      category: job.category,
      description: job.description,
      detail: job.detail,
      requirements: job.requirements,
      benefits: job.benefits,
      status: job.status,
      is_expired: job.isExpired,
    });
  };

  if (isLoading) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách tin tuyển dụng</h3>
        </div>
        <div className="p-10 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-background-200/80 rounded" />
                <div className="h-3 w-1/2 bg-background-200/50 rounded" />
              </div>
              <div className="h-7 w-24 bg-background-200/60 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách tin tuyển dụng</h3>
        </div>
        <div className="flex flex-col items-center py-12 gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            onClick={() => { setIsLoading(true); fetchJobs(); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            <span>Thử lại</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">Danh sách tin tuyển dụng</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center bg-background-100 rounded-lg p-1">
              {(["all", "pending", "approved", "rejected", "expired"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`relative px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    filter === s
                      ? "bg-background-50 text-foreground-900"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {s === "all" ? "Tất cả" : s === "expired" ? "Hết hạn" : statusConfig[s].label}
                  {statusCounts[s] > 0 && (
                    <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${
                      s === "pending" ? "bg-amber-200 text-amber-800" :
                      s === "approved" ? "bg-green-200 text-green-800" :
                      s === "rejected" ? "bg-red-200 text-red-800" :
                      "bg-background-200 text-foreground-600"
                    }`}>
                      {statusCounts[s]}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-background-100 border border-background-200/70 rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-foreground-600 cursor-pointer outline-none focus:border-primary-300 transition-colors"
              >
                <option value="newest">Mới nhất</option>
                <option value="oldest">Cũ nhất</option>
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Chưa có tin tuyển dụng nào</p>
            <p className="text-xs text-foreground-400">Nhấn "Đăng tin" để tạo tin tuyển dụng mới</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Tin tuyển dụng</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Địa điểm</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Lương</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Ngày đăng</th>
                  <th className="text-center px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Trạng thái</th>
                  <th className="text-center px-5 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((job) => {
                  const cfg = job.isExpired
                    ? { label: "Đã hết hạn", className: "bg-background-200 text-foreground-500" }
                    : (statusConfig[job.status] || statusConfig.pending);
                  return (
                    <tr
                      key={job.id}
                      className="border-b border-background-100 last:border-b-0 hover:bg-background-100/60 transition-colors group"
                    >
                      <td className="px-5 py-3.5">
                        <div className="min-w-[200px]">
                          <p className="text-sm font-semibold text-foreground-900 truncate">{job.title}</p>
                          <p className="text-[11px] text-foreground-400 mt-0.5">
                            {typeLabels[job.type] || job.type} &middot; Hết hạn {formatDate(job.expiryDate)}
                          </p>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            <i className="ri-map-pin-line text-foreground-400 text-sm"></i>
                          </div>
                          <span className="text-sm text-foreground-700 truncate">{job.location}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-foreground-900">{job.salary}</span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="text-sm text-foreground-600">{formatDate(job.postedAt)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center whitespace-nowrap">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.className}`}>
                            {job.status === "pending" && !job.isExpired && <i className="ri-time-line text-[10px]"></i>}
                            {job.status === "approved" && !job.isExpired && <i className="ri-check-line text-[10px]"></i>}
                            {job.status === "rejected" && !job.isExpired && <i className="ri-close-line text-[10px]"></i>}
                            {job.isExpired && <i className="ri-timer-line text-[10px]"></i>}
                            {cfg.label}
                          </span>
                          {job.status === "rejected" && job.rejectionReason && (
                            <button
                              onClick={() => setExpandedReason(expandedReason === job.id ? null : job.id)}
                              className="text-[11px] text-red-500 hover:text-red-700 underline cursor-pointer mt-0.5 whitespace-nowrap"
                            >
                              {expandedReason === job.id ? "Ẩn lý do" : "Xem lý do"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-center whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(job)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                          title="Chỉnh sửa tin tuyển dụng"
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-edit-line text-xs"></i>
                          </div>
                          <span>Sửa</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {sorted.map((job) => {
          if (job.status !== "rejected" || !job.rejectionReason || expandedReason !== job.id) return null;
          return (
            <div key={`reason-${job.id}`} className="px-5 py-3 border-t border-red-100 bg-red-50/50 animate-fade-in-up">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-red-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-0.5">Lý do từ chối:</p>
                  <p className="text-sm text-red-600">{job.rejectionReason}</p>
                </div>
              </div>
            </div>
          );
        })}

        <div className="flex items-center justify-between px-5 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-500">
            Hiển thị <strong className="text-foreground-700">{sorted.length}</strong> / {jobs.length} tin tuyển dụng
          </p>
          {jobs.length === 0 && (
            <p className="text-xs text-foreground-400">Đăng tin mới để bắt đầu tuyển dụng</p>
          )}
        </div>
      </div>

      <EditJobModal
        isOpen={!!editingJob}
        onClose={() => setEditingJob(null)}
        job={editingJob}
        onSaved={fetchJobs}
      />
    </>
  );
}